/**
 * Extracts a user-friendly string from an API error response.
 * Handles both plain strings and Pydantic v2's array-of-objects detail format.
 *
 * @param {any} error - The axios error object.
 * @param {string} [defaultMessage] - Fallback message.
 * @returns {string}
 */
export function extractApiError(error, defaultMessage = 'Erro ao salvar') {
  const detail = error?.response?.data?.detail;
  if (!detail) return defaultMessage;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((e) => (typeof e === 'object' && e !== null ? e.msg || JSON.stringify(e) : String(e)))
      .join(' | ');
  }
  return defaultMessage;
}

/**
 * Formats a date string (YYYY-MM-DD or ISO) into Brazilian format (DD/MM/YYYY).
 * Returns a fallback string when the value is empty or invalid.
 *
 * @param {string} dateStr - The date value (e.g. from the API, in YYYY-MM-DD format).
 * @param {string} [fallback] - Value to return when dateStr is empty/invalid.
 * @returns {string}
 */
export function formatDateBR(dateStr, fallback = '-') {
  if (!dateStr) return fallback;

  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
  }

  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return fallback;
  return d.toLocaleDateString('pt-BR');
}

/**
 * Validates a date string coming from an HTML <input type="date"> (YYYY-MM-DD format).
 * Returns null when valid, or a Portuguese error message string when invalid.
 *
 * @param {string} dateStr - The date value from the input (may be empty).
 * @param {string} fieldLabel - Human-readable field name used in the error message.
 * @returns {string|null}
 */
export function validateDateField(dateStr, fieldLabel) {
  if (!dateStr) return null;

  const match = dateStr.match(/^(\d+)-(\d{2})-(\d{2})$/);
  if (!match) return `${fieldLabel}: formato inválido.`;

  const year = parseInt(match[1], 10);
  if (year < 1900 || year > 2099) {
    return `${fieldLabel}: o ano deve ter 4 dígitos (entre 1900 e 2099).`;
  }

  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return `${fieldLabel}: data inválida.`;

  return null;
}

/**
 * Validates multiple date fields at once.
 * Returns the first error message found, or null if all are valid.
 *
 * @param {Array<{value: string, label: string}>} fields
 * @returns {string|null}
 */
export function validateDateFields(fields) {
  for (const { value, label } of fields) {
    const err = validateDateField(value, label);
    if (err) return err;
  }
  return null;
}
